import { getApi, postApi, patchApi, ErrorResponse } from './baseApi';
import { UPLOAD_REQUEST_ENDPOINTS } from '../constants/endpoints';

// pending -> done (every ward uploaded, or marked done by the owner) | rejected | cancelled (taken back)
export type UploadRequestStatus = 'pending' | 'done' | 'rejected' | 'cancelled';

export type UploadRequestJobStatus = 'queued' | 'processing' | 'confirmed' | 'needs_review' | 'failed';

// One PDF the owner uploaded for a request
export interface UploadRequestJob {
  id: string;
  // The ward the PDF turned out to be
  ward_number: string | null;
  filename: string | null;
  status: UploadRequestJobStatus;
  // Why it failed (status failed)
  error: string | null;
  created_at: string;
  finished_at: string | null;
}

export interface UploadRequest {
  id: string;
  status: UploadRequestStatus;
  created_at: string;
  resolved_at: string | null;
  // The campaign (the Admin); same id as /owner/clients/{admin_id}
  admin_id: string;
  admin_name: string | null;
  admin_mobile: string | null;
  // sarpanch | wardpanch | panchayat_samiti | zila_parishad; null = an older account
  admin_post: string | null;
  subscription_status: string | null;
  subscription_plan_id: string | null;
  requested_by: 'admin' | 'manager';
  manager_name: string | null;
  manager_mobile: string | null;
  // English place names, to search the SEC site
  district_name: string | null;
  samiti_name: string | null;
  // Display only: the Hindi name when there is one, else the English one. Never edit this field.
  gram_panchayat: string;
  // The English name (edit this, not gram_panchayat). Optional for an older backend.
  gram_panchayat_en?: string | null;
  // The PDF must be of this gram panchayat; null = the gram panchayat is not checked at all
  gram_panchayat_hi: string | null;
  ward_numbers: number[];
  // Requested wards already uploaded by the owner
  uploaded_wards: number[];
  note: string | null;
  reject_reason: string | null;
  // Detail only: every PDF uploaded for this request, oldest first
  jobs?: UploadRequestJob[];
}

export interface UploadRequestsPage {
  total: number;
  limit: number;
  offset: number;
  items: UploadRequest[];
}

export interface UploadRequestListParams {
  status?: UploadRequestStatus;
  admin_id?: string;
  limit?: number;
  offset?: number;
}

// The menu badge listens for this, so it updates as soon as a request changes here
export const UPLOAD_REQUESTS_CHANGED_EVENT = 'upload-requests-changed';

export const notifyUploadRequestsChanged = () => {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(UPLOAD_REQUESTS_CHANGED_EVENT));
};

export const SEC_PDF_DOWNLOAD_URL = 'https://sec.rajasthan.gov.in/se_pdfdownload.aspx';

export const getUploadRequestsApi = (params: UploadRequestListParams = {}): Promise<UploadRequestsPage> =>
  getApi(UPLOAD_REQUEST_ENDPOINTS.LIST, {
    ...(params.status && { status: params.status }),
    ...(params.admin_id && { admin_id: params.admin_id }),
    limit: Math.min(Math.max(1, params.limit ?? 50), 100),
    offset: Math.max(0, params.offset ?? 0),
  });

// For the menu badge: only the total matters
export const getPendingUploadRequestsCountApi = async (): Promise<number> => {
  const res: UploadRequestsPage = await getApi(UPLOAD_REQUEST_ENDPOINTS.LIST, { status: 'pending', limit: 1 });
  return res?.total ?? 0;
};

export const getUploadRequestApi = (id: string): Promise<UploadRequest> =>
  getApi(UPLOAD_REQUEST_ENDPOINTS.DETAILS(id));

// One PDF per call; can take up to about 2 minutes (the first page is read to find the ward). 202.
export const uploadRequestPdfApi = (id: string, file: File): Promise<UploadRequest> => {
  const form = new FormData();
  form.append('file', file);
  return postApi(UPLOAD_REQUEST_ENDPOINTS.UPLOAD(id), form);
};

// reason: 1-500 characters, shown to the requester in the app
export const rejectUploadRequestApi = (id: string, reason: string): Promise<UploadRequest | ErrorResponse> =>
  patchApi(UPLOAD_REQUEST_ENDPOINTS.REJECT(id), { reason });

// PATCH /owner/upload-requests/{id} body. Every field is optional: send only what changed.
// Empty string or null clears a nullable field. ward_numbers replaces the whole list.
export interface UploadRequestEditBody {
  district_name?: string | null; // up to 150
  samiti_name?: string | null; // up to 150
  gram_panchayat_en?: string; // up to 255, can't be blank (422 GRAM_PANCHAYAT_REQUIRED)
  gram_panchayat_hi?: string | null; // up to 255; what every PDF's header is checked against
  ward_numbers?: number[]; // 1-100 items, each 1-100
  note?: string | null; // up to 500
}

export const UPLOAD_REQUEST_EDIT_LIMITS = {
  district_name: 150,
  samiti_name: 150,
  gram_panchayat_en: 255,
  gram_panchayat_hi: 255,
  note: 500,
  maxWards: 100,
  minWard: 1,
  maxWard: 100,
} as const;

// Only a pending request can be changed (409 REQUEST_NOT_PENDING). The answer is the full detail.
// 409 REQUEST_ALREADY_PENDING: extra.ward_numbers are wards another pending request already asks for.
export const editUploadRequestApi = (id: string, body: UploadRequestEditBody): Promise<UploadRequest> =>
  patchApi(UPLOAD_REQUEST_ENDPOINTS.EDIT(id), body);

// Mark done early, e.g. a ward is not on the SEC site
export const completeUploadRequestApi = (id: string): Promise<UploadRequest | ErrorResponse> =>
  patchApi(UPLOAD_REQUEST_ENDPOINTS.COMPLETE(id));
